import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Sparkles, RefreshCw, LogIn, Eye, EyeOff, ChevronLeft, ChevronRight } from "lucide-react";
import "./ChrisTurnerMode.css";

// Server-side TTS via /api/tts (OpenAI tts-1). The returned MP3 is played
// through a regular HTMLAudioElement so it shares the same iOS audio session
// category as the music beat — both will route to the user's Bluetooth speaker.
// (Web Speech API was previously used here but iOS routes AVSpeechSynthesizer
//  to the iPhone's internal speaker even when A2DP is connected.)

const LOADING_MESSAGES = [
  "Chris réfléchit...",
  "Cherche dans la salle...",
  "Choisit les 5 mots...",
  "Imagine les anecdotes...",
  "Quasi prêt...",
];

export default function ChrisTurnerMode({ backendApi, isAuthenticated, setDashboardCollapsed, dashboardAutoHideLockRef, audioPlaying, setBeatDuck }) {
  const [words, setWords] = useState([]);
  const [portraitIds, setPortraitIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [textHidden, setTextHidden] = useState(false);
  const textHiddenRef = useRef(false);
  useEffect(() => { textHiddenRef.current = textHidden; }, [textHidden]);
  const cancelledRef = useRef(false);

  // Karaoke-style pulse animation on the displayed word. We increment a counter
  // each time we want the word to pulse; the JSX uses the counter value as a
  // React key so the same animation can replay even if other props are stable.
  const [wordPulseKey, setWordPulseKey] = useState(0);
  const pulseTimeoutsRef = useRef([]);
  const clearPulseTimers = useCallback(() => {
    pulseTimeoutsRef.current.forEach(clearTimeout);
    pulseTimeoutsRef.current = [];
  }, []);

  // TTS audio element (server-side TTS routed through Bluetooth speakers on iOS).
  // Created once on mount; kept in a ref so re-renders don't re-create it.
  const ttsAudioRef = useRef(null);
  useEffect(() => {
    if (ttsAudioRef.current === null && typeof window !== "undefined") {
      ttsAudioRef.current = new Audio();
      ttsAudioRef.current.preload = "auto";
    }
  }, []);

  const cancelTTS = useCallback(() => {
    const a = ttsAudioRef.current;
    if (!a) return;
    pulseTimeoutsRef.current.forEach(clearTimeout);
    pulseTimeoutsRef.current = [];
    // Always restore the beat volume in case the TTS was interrupted mid-play
    if (setBeatDuck) setBeatDuck(false);
    try {
      a.pause();
      a.currentTime = 0;
      if (a.src && a.src.startsWith("blob:")) {
        try { URL.revokeObjectURL(a.src); } catch { /* noop */ }
      }
      a.removeAttribute("src");
      a.load();
    } catch { /* noop */ }
  }, [setBeatDuck]);

  // Fetch TTS MP3 from backend and play through the same <audio> element.
  // Returns a promise that resolves when playback ends (or fails).
  // We wait for `canplaythrough` before calling .play() to avoid iOS Safari
  // truncating the first few hundred ms of audio (which often eats short
  // words at the very beginning, like "Cornemuse." or "Phoque.").
  const speakFrench = useCallback(async (text) => {
    if (!text) return;
    const a = ttsAudioRef.current;
    if (!a) return;
    let blobUrl;
    try {
      const token = localStorage.getItem("ff_auth_token");
      const resp = await fetch(`${backendApi}/tts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) return;
      if (cancelledRef.current) return;
      const blob = await resp.blob();
      blobUrl = URL.createObjectURL(blob);
      cancelTTS();           // stop any in-flight playback
      if (cancelledRef.current) {
        URL.revokeObjectURL(blobUrl);
        return;
      }
      a.src = blobUrl;
      a.load();              // force the browser to start fetching the blob

      // Wait until the audio has enough buffer to play through without stalling.
      // Fallback timeout (1.5s) in case the event never fires on some browsers.
      await new Promise((readyResolve) => {
        let settled = false;
        const settle = () => {
          if (settled) return;
          settled = true;
          a.removeEventListener("canplaythrough", settle);
          a.removeEventListener("loadeddata", settle);
          a.removeEventListener("error", settle);
          readyResolve();
        };
        a.addEventListener("canplaythrough", settle, { once: true });
        a.addEventListener("loadeddata", settle, { once: true });
        a.addEventListener("error", settle, { once: true });
        setTimeout(settle, 1500);
      });

      if (cancelledRef.current) {
        URL.revokeObjectURL(blobUrl);
        return;
      }

      await new Promise((resolve) => {
        const done = () => {
          a.removeEventListener("ended", done);
          a.removeEventListener("error", done);
          if (blobUrl) try { URL.revokeObjectURL(blobUrl); } catch { /* noop */ }
          // Restore beat volume when TTS finishes (or errors out)
          if (setBeatDuck) setBeatDuck(false);
          resolve();
        };
        a.addEventListener("ended", done);
        a.addEventListener("error", done);
        // Schedule ONE word-pulse to match the single TTS occurrence.
        // Skipped during recitation ("Tout masquer") because the word is
        // hidden anyway. The 400ms delay accounts for the leading "..."
        // silent gap in the TTS text.
        clearPulseTimers();
        if (!textHiddenRef.current) {
          const t1 = setTimeout(() => setWordPulseKey(k => k + 1), 400);
          pulseTimeoutsRef.current.push(t1);
        }
        // Duck the music beat by -30% while the TTS speaks
        if (setBeatDuck) setBeatDuck(true);
        a.play().catch(done);
      });
    } catch {
      if (blobUrl) try { URL.revokeObjectURL(blobUrl); } catch { /* noop */ }
      if (setBeatDuck) setBeatDuck(false);
    }
  }, [backendApi, cancelTTS, clearPulseTimers, setBeatDuck]);

  // iOS requires user-gesture audio "unlock" the first time. Touching the
  // audio element directly inside the click handler primes it for later
  // programmatic play() calls (e.g. after an awaited fetch).
  const warmupTTS = useCallback(() => {
    const a = ttsAudioRef.current;
    if (!a) return;
    try {
      // A single play()/pause() pair within the user gesture is enough to
      // satisfy iOS autoplay policy for subsequent programmatic plays.
      a.muted = true;
      const p = a.play();
      if (p && typeof p.then === "function") {
        p.then(() => { a.pause(); a.muted = false; }).catch(() => { a.muted = false; });
      } else {
        a.pause();
        a.muted = false;
      }
    } catch { /* noop */ }
  }, []);

  // Loading message: just show the first one. Previously cycled every 2s via
  // setInterval+setState in useEffect, but the linter (v2) disallows that
  // pattern, and a static loader is just as user-friendly.
  // (loadingMsgIdx kept as a state for compatibility with the JSX below.)
  // No effect — we just set the initial value once via useState.

  const generate = useCallback(async () => {
    if (!isAuthenticated) return;
    cancelTTS();
    cancelledRef.current = true;          // stop any running auto-play
    setActiveIdx(-1);
    setTextHidden(false);                 // re-show text on new generation
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("ff_auth_token");
      const resp = await axios.post(
        `${backendApi}/llm/inspiration`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWords(resp.data.words || []);
      setPortraitIds(resp.data.portrait_ids || []);
      // Trigger auto-play of card 0 (was previously in a useEffect on words,
      // moved here to keep setState out of useEffect)
      cancelledRef.current = false;
      setActiveIdx(0);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Erreur inconnue";
      setError(typeof detail === "string" ? detail : "Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, [backendApi, isAuthenticated, cancelTTS]);

  // Auto-play: read each card (mot + info_public) on a tap-driven cycle.
  // Tapping the screen during a card cuts to the next card.
  // We send the entire text as ONE utterance: Chrome/Safari have a documented
  // race condition where chaining multiple speak() calls in a loop causes
  // `onend` to fire prematurely on segments 2+, so only the first segment plays.
  const playCard = useCallback(async (idx) => {
    if (idx < 0 || idx >= words.length) return;
    // In recitation mode (text hidden), navigation must NOT read the word aloud.
    // The whole point is that the user recalls it on their own.
    if (textHiddenRef.current) return;
    const w = words[idx];
    // TTS text — the word once with a leading silent gap (via "..." prefix)
    // to absorb the ~150-300ms of buffer warm-up on iOS that can otherwise
    // clip the start of short words. Combined with awaiting `canplaythrough`
    // before .play(), this is enough on modern iOS to avoid word truncation.
    const fullText = `... ${w.mot} !`;
    cancelTTS();
    if (cancelledRef.current) return;
    await speakFrench(fullText);
  }, [words, cancelTTS, speakFrench]);

  // Whenever activeIdx changes, play that card
  useEffect(() => {
    if (activeIdx < 0 || activeIdx >= words.length) return;
    cancelledRef.current = false;
    playCard(activeIdx);
    return () => {
      cancelledRef.current = true;
      cancelTTS();
    };
  }, [activeIdx, playCard, words.length]);

  // (Auto-play on new words is triggered directly in generate() now, to keep
  // setState out of useEffect bodies.)

  // Auto-collapse dashboard when cards are visible + lock against window taps.
  useEffect(() => {
    const hasCards = words.length > 0;
    if (dashboardAutoHideLockRef) dashboardAutoHideLockRef.current = hasCards;
    if (hasCards && setDashboardCollapsed) setDashboardCollapsed(true);
    return () => {
      if (dashboardAutoHideLockRef) dashboardAutoHideLockRef.current = false;
    };
  }, [words.length, setDashboardCollapsed, dashboardAutoHideLockRef]);

  // Note: the previous behavior auto-hid the word + anecdote as soon as the beat
  // (or metronome) started playing. Removed at user's request — the user now
  // decides when to hide via the "Tout masquer" button.

  // Stop TTS on unmount
  useEffect(() => () => { cancelledRef.current = true; cancelTTS(); }, []);

  const handleAdvance = () => {
    if (words.length === 0) return;
    cancelTTS();
    cancelledRef.current = true;
    const nextIdx = Math.min(activeIdx + 1, words.length - 1);
    // If we're already on the last card, replay it directly (state would be a no-op)
    if (nextIdx === activeIdx) {
      cancelledRef.current = false;
      playCard(activeIdx);
      return;
    }
    setActiveIdx(nextIdx);
    setTimeout(() => { cancelledRef.current = false; }, 0);
  };

  const replayCard = (idx) => {
    cancelTTS();
    cancelledRef.current = false;
    // Call playCard directly: setActiveIdx would be a no-op when idx === activeIdx
    // (React skips re-render → useEffect doesn't refire → playCard never runs).
    if (idx !== activeIdx) setActiveIdx(idx);
    playCard(idx);
  };

  // ===== RENDER =====
  if (!isAuthenticated) {
    return (
      <div className="chris-turner-container" data-testid="chris-turner-container">
        <div className="chris-turner-auth-required">
          <Sparkles size={40} strokeWidth={1.5} />
          <h2>Mode Chris TURNER</h2>
          <p>
            Connecte-toi pour générer 5 propositions de freestyle façon Chris Turner
            — mots, groupes, concepts, expressions ou situations — avec leur mise en situation.
          </p>
          <div className="chris-turner-auth-hint">
            <LogIn size={16} />
            <span>Bouton &quot;Connexion&quot; en haut à droite</span>
          </div>
        </div>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div className="chris-turner-container" data-testid="chris-turner-container">
        <div className="chris-turner-empty">
          <h2>Mode Chris TURNER</h2>
          <p className="chris-turner-tagline">
            5 propositions variées — mot, groupe, concept, expression ou situation —
            chacune avec une mise en situation pour t&apos;entraîner comme si tu étais sur scène.
          </p>
          <button
            className={`chris-turner-cta ${loading ? "ct-loading" : ""}`}
            onClick={() => { warmupTTS(); generate(); }}
            disabled={loading}
            data-testid="chris-turner-generate-btn"
          >
            {loading ? (
              <>
                <RefreshCw size={18} className="ct-spin" />
                <span key={loadingMsgIdx} className="ct-loading-msg">
                  {LOADING_MESSAGES[loadingMsgIdx]}
                </span>
              </>
            ) : (
              <>
                <Sparkles size={18} />
                Générer 5 propositions
              </>
            )}
          </button>

          {loading && (
            <div className="chris-turner-skeleton" data-testid="chris-turner-skeleton">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="ct-skeleton-card" style={{ animationDelay: `${i * 0.12}s` }}>
                  <div className="ct-skeleton-num" />
                  <div className="ct-skeleton-mot" />
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="chris-turner-error" data-testid="chris-turner-error">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="chris-turner-container"
      data-testid="chris-turner-container"
      onClick={() => { warmupTTS(); handleAdvance(); }}
    >
      <div className="chris-turner-toolbar">
        <div className="chris-turner-progress">
          {words.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`chris-turner-dot ${i === activeIdx ? "active" : ""} ${i < activeIdx ? "done" : ""}`}
              data-testid={`chris-turner-dot-${i}`}
              onClick={(e) => { e.stopPropagation(); warmupTTS(); replayCard(i); }}
              title={`Aller au mot ${i + 1}`}
            />
          ))}
        </div>
        {textHidden ? (
          <button
            className="chris-turner-reveal"
            onClick={(e) => { e.stopPropagation(); setTextHidden(false); }}
            data-testid="chris-turner-reveal-btn"
            title="Réafficher les mots et les anecdotes"
          >
            <Eye size={14} />
            <span>Tout revoir</span>
          </button>
        ) : (
          <button
            className="chris-turner-reveal chris-turner-reveal-hide"
            onClick={(e) => { e.stopPropagation(); cancelTTS(); setTextHidden(true); }}
            data-testid="chris-turner-hide-btn"
            title="Masquer mots et anecdotes pour s'entraîner à la mémorisation"
          >
            <EyeOff size={14} />
            <span>Tout masquer</span>
          </button>
        )}
        <button
          className="chris-turner-regenerate"
          onClick={(e) => { e.stopPropagation(); warmupTTS(); generate(); }}
          disabled={loading}
          data-testid="chris-turner-regenerate-btn"
        >
          {loading ? <RefreshCw size={14} className="ct-spin" /> : <RefreshCw size={14} />}
          <span>Regénérer</span>
        </button>
      </div>

      <div className="chris-turner-stage">
        <button
          type="button"
          className="chris-turner-arrow chris-turner-arrow-prev"
          onClick={(e) => { e.stopPropagation(); warmupTTS(); replayCard(Math.max(0, activeIdx - 1)); }}
          disabled={activeIdx <= 0}
          data-testid="chris-turner-prev-btn"
          title="Mot précédent"
          aria-label="Mot précédent"
        >
          <ChevronLeft size={24} />
        </button>

        <div className={`chris-turner-cards ${textHidden ? "ct-text-hidden" : ""}`} data-testid="chris-turner-cards">
          {words.map((w, i) => {
            if (i !== activeIdx) return null;
            const portraitId = portraitIds[i];
            return (
              <div
                key={`${w.mot}-${i}`}
                className={`chris-turner-card ${i === activeIdx ? "active" : ""}`}
                data-testid={`chris-turner-card-${i}`}
                onClick={(e) => {
                  e.stopPropagation();
                  warmupTTS();
                  replayCard(i);
                }}
              >
                {portraitId ? (
                  <img
                    src={`${backendApi}/portrait/${portraitId}`}
                    alt=""
                    className="chris-turner-card-portrait"
                    data-testid={`chris-turner-portrait-${i}`}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="chris-turner-card-num">{i + 1}</div>
                )}
                <div className="chris-turner-card-content">
                  {!textHidden && (
                    <div
                      key={`mot-${i}-${wordPulseKey}`}
                      className={`chris-turner-card-mot ct-pulse ${
                        w.mot.length > 20 ? "ct-mot-long" : w.mot.length > 12 ? "ct-mot-medium" : ""
                      }`}
                      data-testid={`chris-turner-mot-${i}`}
                    >
                      {w.mot}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="chris-turner-arrow chris-turner-arrow-next"
          onClick={(e) => { e.stopPropagation(); warmupTTS(); replayCard(Math.min(words.length - 1, activeIdx + 1)); }}
          disabled={activeIdx >= words.length - 1}
          data-testid="chris-turner-next-btn"
          title="Mot suivant"
          aria-label="Mot suivant"
        >
          <ChevronRight size={24} />
        </button>
      </div>

      <div className="chris-turner-hint">
        {textHidden
          ? "Mémorisation en cours — Tape sur \"Tout revoir\" pour réafficher"
          : (activeIdx < words.length - 1
            ? `Tap pour passer au mot suivant (${activeIdx + 1}/${words.length})`
            : "Dernier mot — Tap regénérer pour relancer")}
      </div>
    </div>
  );
}
